import { useSetReplayUserPreference } from '@common/Replay/utils';
import { usePersistentCallback } from '@prophecy/utils/react/hooks';
import { createContext, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useAppMetadata } from '../../context/appMetadata';
import { useGlobalVariables } from '../../context/globalConfig/securedConfigHook';
import { useMixPanel } from '../../context/mixpanel/context';
import { UserDetails } from '../../context/types';
import { useGraphQlMutation } from '../../data/util';
import { openFabricSetup } from '../Fabric/FabricSetup';
import { useUserFabrics } from '../Fabric/hooks';
import { Entity } from '../types/Entity';
import { getIDEUrl, Private_Routes } from '../url';
import { Onboarding } from '../user-analytics';
import { NEW_PIPELINE_FLOW_ID, PIPELINE_FLOW_ID, SQL_COPILOT_FLOW_ID, SQL_PIPELINE_FLOW_ID } from './constants';
import { UpdateOnboardingStatusMutation } from './query';
import { Processes } from './sql/constants';
import { OnboardingStatus } from './types';

type OnboardingValueType = string | boolean;

const OnboardingContext = createContext<OnboardingValueType>(false);
export const OnboardingProvider = OnboardingContext.Provider;

function useOnboardingFlag() {
  return useContext(OnboardingContext);
}
export function isPipelineOnboardingEnabled(onboarding: OnboardingValueType) {
  return onboarding === PIPELINE_FLOW_ID;
}
function isNewPipelineOnboardingEnabled(onboarding: OnboardingValueType) {
  return onboarding === NEW_PIPELINE_FLOW_ID;
}
function isSqlOnboardingEnabled(onboarding: OnboardingValueType) {
  return onboarding === SQL_PIPELINE_FLOW_ID;
}
function isSqlOnboardingCopilotEnabled(onboarding: OnboardingValueType) {
  return onboarding === SQL_COPILOT_FLOW_ID;
}
export function useIsSqlOnboardingEnabled() {
  const onboarding = useOnboardingFlag();
  return isSqlOnboardingEnabled(onboarding);
}
export function useIsSqlOnboardingCopilotEnabled() {
  const onboarding = useOnboardingFlag();
  return isSqlOnboardingCopilotEnabled(onboarding);
}
export function useIsPipelineOnboardingEnabled() {
  const onboarding = useOnboardingFlag();
  return isPipelineOnboardingEnabled(onboarding);
}
export function useIsNewPipelineOnboardingEnabled() {
  const onboarding = useOnboardingFlag();
  return isNewPipelineOnboardingEnabled(onboarding);
}
export function useIsOnboardingEnabled() {
  const isPipelineOnboardingEnabled = useIsPipelineOnboardingEnabled();
  const isSqlOnboardingEnabled = useIsSqlOnboardingEnabled();
  const isSqlOnboardingCopilotEnabled = useIsSqlOnboardingCopilotEnabled();

  return isSqlOnboardingEnabled || isPipelineOnboardingEnabled || isSqlOnboardingCopilotEnabled;
}

export function useOnboardingDetail() {
  const preLoginData = useAppMetadata();
  const onboardingDetail = (preLoginData.user as UserDetails).onboardingDetail;
  const userOnboardingDetails = preLoginData.user?.userOnboardingDetails;

  if (onboardingDetail?.project) {
    const {
      datasetDirectoryPath,
      fabric,
      sqlFabric,
      sqlProject,
      analystFabric,
      project: {
        pipelines: [pipeline]
      }
    } = onboardingDetail;
    const result = {
      pipeline,
      fabric,
      sqlFabric,
      sqlProject,
      datasetDirectoryPath,
      analystProject: 'analystProject' in onboardingDetail ? onboardingDetail.analystProject : undefined,
      analystFabric,
      userOnboardingDetails
    };

    return result;
  }

  return {
    pipeline: undefined,
    fabric: undefined,
    sqlFabric: undefined,
    sqlProject: undefined,
    datasetDirectoryPath: undefined,
    analystProject: undefined,
    analystFabric: undefined,
    userOnboardingDetails: undefined
  };
}
function useCheckIfNewPipelineOnBoardingEnabled(params: URLSearchParams) {
  const onboardingStr = params.get('onboarding');
  return Boolean(onboardingStr);
}
export function useSparkOnboardingValue(uid: string) {
  const [params] = useSearchParams();
  const isNewPipelineOnboardingEnabled = useCheckIfNewPipelineOnBoardingEnabled(params);
  const { pipeline: demoPipeline, fabric } = useOnboardingDetail();
  const isPipelineOnboardingEnabled = demoPipeline && fabric && uid === demoPipeline._id;
  const onboarding = isNewPipelineOnboardingEnabled
    ? NEW_PIPELINE_FLOW_ID
    : isPipelineOnboardingEnabled
      ? PIPELINE_FLOW_ID
      : false;

  return onboarding;
}

export const useOnBoardingSkipFlow = () => {
  const { pipeline: demoPipeline, sqlProject } = useOnboardingDetail();

  const globalConf = useGlobalVariables();
  const { skipOnboarding } = useSetReplayUserPreference();
  const isProfessionalEditionEnabled = globalConf.isProfessionalEditionEnabled;
  const updateOnboardingStatus = useUpdateOnboardingStatus();
  const { track } = useMixPanel();
  const sqlOnboardingUrl = Private_Routes.SQL_IDE.getUrl(
    {
      uid: sqlProject?._id as string
    },
    { entity: Entity.Model as string, name: Processes.CustomerNation.label }
  );
  const sparkOnboardingUrl = getIDEUrl({ uid: demoPipeline?._id as string, entity: Entity.Pipeline });
  const navigate = useNavigate();
  const fabrics = useUserFabrics();
  async function onSkip() {
    const sparkOnboardingStatus = globalConf.getSparkOnboardingStatus;
    const sqlOnboardingStatus = globalConf.getSqlOnboardingStatus;

    if (isProfessionalEditionEnabled) {
      skipOnboarding();
      track(
        Onboarding.onboarding_home.events.onboarding_home_skip,
        {
          onboarding_skip_sql: true,
          onboarding_skip_spark: true,
          onboarding_btn_clicked: true
        },
        Onboarding.onboarding_home.event_category
      );
      return;
    }

    const isSqlOnboardingPending = sqlOnboardingStatus === OnboardingStatus.READY_TO_BEGIN;
    const isSparkOnboardingPending = sparkOnboardingStatus === OnboardingStatus.READY_TO_BEGIN;
    if (isSparkOnboardingPending && isSqlOnboardingPending) {
      await updateOnboardingStatus({
        lastFlowCompleted: OnboardingStatus.ONBOARDING_PIPELINE,
        lastFlowCompletedSql: OnboardingStatus.ONBOARDING_PIPELINE
      });
      track(
        Onboarding.onboarding_home.events.onboarding_home_skip,
        {
          onboarding_skip_sql: true,
          onboarding_skip_spark: true,
          onboarding_btn_clicked: true
        },
        Onboarding.onboarding_home.event_category
      );
    } else {
      track(
        Onboarding.onboarding_home.events.onboarding_home_skip,
        {
          onboarding_btn_clicked: true
        },
        Onboarding.onboarding_home.event_category
      );
    }

    openFabricSetup({ navigate, fabrics, globalConf });
  }
  return { onSkip, sqlOnboardingUrl, sparkOnboardingUrl, track, navigate };
};

export function useUpdateOnboardingStatus() {
  const globalConf = useGlobalVariables();
  const aspectMutation = useGraphQlMutation(UpdateOnboardingStatusMutation);
  return usePersistentCallback(async function (payload: {
    lastFlowCompleted?: OnboardingStatus;
    lastFlowCompletedSql?: OnboardingStatus;
  }) {
    await aspectMutation.mutateAsync(payload);
    if (payload.lastFlowCompleted) {
      globalConf.setSparkOnboardingStatus(payload.lastFlowCompleted);
    }
    if (payload.lastFlowCompletedSql) {
      globalConf.setSqlOnboardingStatus(payload.lastFlowCompletedSql);
    }
  });
}

/**
 * This hook is used to check if the user has only one fabric and it is the analyst fabric
 * @returns true if the user has only one fabric and it is the analyst fabric, false otherwise, this will be true only for the new onboarding flow
 */
export const useHasOnlyAnalystFabric = () => {
  const { analystFabric } = useOnboardingDetail();
  const userFabrics = useUserFabrics();
  return userFabrics.length === 1 && userFabrics[0]._id === analystFabric?._id;
};

/**
 * This hook is used to check if the user has an analyst fabric
 * @returns true if the user has an analyst fabric, false otherwise, analyst fabric is only available for the new onboarding flow
 */
export const useHasAnalystFabric = () => {
  const { analystFabric } = useOnboardingDetail();
  return Boolean(analystFabric);
};
